// Shared tool metadata for isomorphic intent matching (no browser-only deps)
// Only includes id, name, description, triggers, and optional route.

export interface ToolMeta {
  id: string;
  name: string;
  description: string;
  triggers: string[];
  route?: string;
}

export const toolsMetadata: ToolMeta[] = [
  // Marketing & Communications (mirror backend ids for consistency)
  {
    id: "marketing.social_posts",
    name: "Social Media Post Generator",
    description: "Generate engaging social media content for multiple platforms.",
    triggers: [
      "social post",
      "facebook post",
      "linkedin post",
      "twitter post",
      "instagram post",
      "threads",
      "schedule post",
      "post now",
      "publish now"
    ],
    route: "/marketing/social-media-planner",
  },
  {
    id: "marketing.social_planner",
    name: "Social Media Planner",
    description: "Plan, schedule, and publish posts across platforms.",
    triggers: ["social planner", "schedule posts", "content calendar", "one-click publish"],
    route: "/marketing/social-media-planner",
  },
  {
    id: "communications.email.send",
    name: "Email Sender",
    description: "Send an email via your connected Gmail or SMTP account.",
    triggers: [
      "email",
      "send email",
      "compose email",
      "write email",
      "email to",
      "gmail",
      "send message",
      "outreach email"
    ],
  },
  {
    id: "marketing.ai_website",
    name: "AI Website Generator",
    description: "Create professional websites from a short description.",
    triggers: [
      "website generator",
      "website builder",
      "build website",
      "build a website",
      "build my website",
      "create website",
      "create a website",
      "create my website",
      "make a website",
      "make website",
      "landing pages",
      "homepage"
    ],
    route: "/marketing/ai-website-generator",
  },
  {
    id: "marketing.images.text_to_image",
    name: "Text to Image Generator",
    description: "Generate images from text prompts.",
    triggers: ["text to image", "generate image", "create image", "ai image"],
    route: "/marketing/text-to-image-generator",
  },
  {
    id: "marketing.logo.generate",
    name: "Logo Generator",
    description: "Generate logos from a brief.",
    triggers: ["logo", "brandmark", "generate logo", "create logo"],
    route: "/marketing/brand-kit-creator#logo-design",
  },
  {
    id: "marketing.business_name",
    name: "Business Name Generator",
    description: "Generate creative and memorable business names.",
    triggers: ["business name", "name ideas", "brand name", "company name", "startup name"],
    route: "/marketing/business-name-generator",
  },
  {
    id: "marketing.linkedin_profile",
    name: "LinkedIn Profile Enhancer",
    description: "Optimize your LinkedIn profile with AI-generated content.",
    triggers: ["linkedin profile", "linkedin headline", "linkedin summary", "experience bullets"],
    route: "/marketing/linkedin-profile-enhancer",
  },
  {
    id: "marketing.linkedin_company_profile",
    name: "LinkedIn Business Profile Enhancer",
    description: "Optimize your company's LinkedIn page with AI-generated content.",
    triggers: ["linkedin company", "company page", "company description", "about company"],
    route: "/marketing/linkedin-business-profile-enhancer",
  },
  {
    id: "marketing.press_release",
    name: "Press Release Generator",
    description: "Generate professional press releases for announcements.",
    triggers: ["press release", "PR", "announcement", "media"],
    route: "/marketing/press-release-generator",
  },
  {
    id: "marketing.blog_creator",
    name: "Blog Post Creator",
    description: "Create blog posts from a brief and align to your content strategy.",
    triggers: ["blog post", "article", "content brief", "outline", "blog idea"],
    route: "/marketing/blog-post-creator",
  },
  {
    id: "marketing.seo_analyzer",
    name: "SEO Analyzer",
    description: "Analyze SEO performance and get actionable insights.",
    triggers: ["seo", "audit", "on-page", "technical seo", "site speed", "core web vitals"],
    route: "/marketing/seo-analyzer",
  },
  {
    id: "marketing.topical_authority",
    name: "Topical Authority Generator",
    description: "Generate SEO-optimized pillar topics and supporting articles.",
    triggers: ["topical authority", "pillar articles", "topic clusters", "silo"],
    route: "/marketing/topical-authority-generator",
  },
  {
    id: "marketing.text_to_video",
    name: "Text to Video Generator",
    description: "Turn scripts into marketing videos with AI.",
    triggers: ["text to video", "ai video", "script to video", "shorts", "reels"],
    route: "/marketing/text-to-video-generator",
  },
  {
    id: "marketing.video_recorder",
    name: "Video Recorder",
    description: "Record professional videos with screen or camera for marketing.",
    triggers: ["record video", "screen record", "camera", "tutorial video", "presentation"],
    route: "/marketing/video-recorder",
  },
  {
    id: "marketing.web_analytics",
    name: "Web Traffic Analytics",
    description: "Analyze search performance across Google and Bing.",
    triggers: ["analytics", "google search console", "bing", "rankings", "impressions", "clicks"],
    route: "/marketing/web-traffic-analytics",
  },
  {
    id: "marketing.cold_email",
    name: "Cold Email Tool",
    description: "Create personalized cold emails and follow-ups.",
    triggers: ["cold email", "outreach", "prospect", "personalize", "follow up"],
    route: "/email",
  },
  {
    id: "marketing.cold_email.create_campaign",
    name: "Cold Email Campaign Creator",
    description: "Create and launch cold email campaigns with AI-generated copy.",
    triggers: [
      "cold email campaign",
      "email campaign",
      "email sequence",
      "outreach campaign",
      "create cold email",
      "launch outreach"
    ],
    route: "/email",
  },
  {
    id: "crm.telemarketing.book_meetings_campaign",
    name: "AI Telemarketing (Book Meetings)",
    description: "Create an AI telemarketing campaign to book meetings using the Explanatum methodology.",
    triggers: [
      "telemarketing",
      "cold calling",
      "cold call",
      "outbound calling",
      "call prospects",
      "ai caller",
      "book meetings",
      "book demos",
      "schedule calls",
      "generate pipeline"
    ],
    route: "/crm/telemarketing",
  },
  {
    id: "marketing.seo_content_hub",
    name: "SEO Content Hub",
    description: "Generate topical authority maps, create optimized blog posts, and manage content library.",
    triggers: ["blog", "article", "content", "topical authority", "pillar articles", "seo content"],
    route: "/marketing/seo-content-hub",
  },
  {
    id: "marketing.launchpad",
    name: "Business Launchpad",
    description: "Plan and track your business launch with tasks and milestones.",
    triggers: ["launch plan", "go to market", "gtm", "milestones", "launch checklist"],
    route: "/launchpad",
  },
  {
    id: "marketing.pod_designer",
    name: "Print-on-Demand Designer",
    description: "Create custom designs for t-shirts, posters, and merchandise.",
    triggers: ["print on demand", "t-shirt design", "merch", "mockups", "pod"],
    route: "/marketing/print-on-demand",
  },
  {
    id: "marketing.affiliate_mgmt",
    name: "Affiliate Management",
    description: "Set up and manage your affiliate program.",
    triggers: ["affiliate", "referrals", "commissions", "signup form", "payouts"],
    route: "/marketing/affiliate-management",
  },
  {
    id: "marketing.leadgen_playbooks",
    name: "Inbound Lead Gen Playbooks",
    description: "Plan inbound campaigns across SEO, content, lead magnets and nurture.",
    triggers: ["lead generation", "inbound", "lead magnet", "conversion funnel", "landing page optimization"],
    route: "/marketing",
  },
  {
    id: "finance.price_calculator",
    name: "Price Calculator",
    description: "Create and manage pricing scenarios.",
    triggers: ["price calculator", "pricing scenario", "set price", "pricing"],
    route: "/finance/price-calculator",
  },
  {
    id: "finance.profit_margin",
    name: "Profit Margin Analyzer",
    description: "Create and analyze profit margin records.",
    triggers: ["profit margin", "margin", "gross margin", "markup"],
    route: "/finance/profit-margin",
  },
  {
    id: "finance.breakeven",
    name: "Break-Even Calculator",
    description: "Create and analyze break-even calculations.",
    triggers: ["break even", "breakeven", "break-even", "fixed costs"],
    route: "/finance/break-even",
  },
  {
    id: "finance.cash_flow",
    name: "Cash Flow Forecaster",
    description: "Create and update cash flow projections.",
    triggers: ["cash flow", "cashflow", "projection", "forecast"],
    route: "/finance/cash-flow",
  },
  {
    id: "finance.pricing_strategy",
    name: "Pricing Strategy Advisor",
    description: "Create and manage pricing strategies.",
    triggers: ["pricing strategy", "value-based pricing", "competitive pricing"],
    route: "/finance/pricing-strategy",
  },
  {
    id: "finance.sales_tracker",
    name: "Sales Tracker",
    description: "Record daily sales and view recent transactions.",
    triggers: ["sales tracker", "add sales", "daily sales", "record sale"],
    route: "/finance/sales-tracker",
  },
  {
    id: "finance.expense_tracker",
    name: "Expense Tracker",
    description: "Record expenses, categorize costs, and review spending.",
    triggers: ["expense tracker", "add expense", "record cost", "spending"],
    route: "/finance/expense-tracker",
  },
  {
    id: "operations.supplier_finder",
    name: "Supplier Finder",
    description: "Find licensed trade suppliers.",
    triggers: ["supplier", "find supplier", "wholesaler", "trade supplier"],
    route: "/finance/supplier-finder",
  },
  {
    id: "operations.risk_manager",
    name: "Risk Manager",
    description: "Identify and assess business risks.",
    triggers: ["risk", "risk register", "risk assessment", "business risks"],
    route: "/finance/risk-manager",
  },
];

export type ToolId = typeof toolsMetadata[number]["id"]; 
export function getToolMetaById(id: string): ToolMeta | undefined {
  return toolsMetadata.find(t => t.id === id);
}
